<script setup lang="ts">
import { computed, inject, ref, useId } from 'vue'
import { useRoute } from 'vue-router'
import AdvancedSettings from './AdvancedSettings.vue'
import { inputKeysToShow, withInput } from './sub-workflow-inputs'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import { useProjectWorkflows } from '@/queries/projects'
import { useWorkflowInputKeys } from '@/queries/workflows'
import type { SubWorkflowServiceModel } from '@/types'
import type { ScopeDescriptor } from '@/types/bbel-types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'

const props = defineProps<{
  modelValue: SubWorkflowServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: SubWorkflowServiceModel]
}>()

function update(changes: Partial<SubWorkflowServiceModel>) {
  emit('update:modelValue', { ...props.modelValue, ...changes })
}

const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => scopeCtx?.value ?? EMPTY_BBEL_SCOPE)

// The builder's route names the project and the workflow being edited (BuilderView.vue:93-94).
const route = useRoute()
const projectId = computed(() => (route.params.projectid as string) || '')
const editedWorkflowId = computed(() => (route.params.workflowid as string) || '')

const { projectWorkflows } = useProjectWorkflows(projectId, ref(true))
const callableWorkflows = computed(() =>
  projectWorkflows.value.filter((workflow) => workflow.id !== editedWorkflowId.value),
)

const calledWorkflowId = computed(() => props.modelValue.workflowId || null)
const { inputKeys } = useWorkflowInputKeys(projectId, calledWorkflowId)
const inputRows = computed(() => inputKeysToShow(inputKeys.value?.keys ?? [], props.modelValue.inputs))

const workflowSelectId = useId()
</script>

<template>
  <div>
    <div class="mb-3">
      <label class="form-label" :for="workflowSelectId">Workflow</label>
      <select
        :id="workflowSelectId"
        class="form-select"
        :value="modelValue.workflowId"
        @change="update({ workflowId: ($event.target as HTMLSelectElement).value })"
      >
        <option value="" disabled>Choose a workflow</option>
        <option v-for="workflow in callableWorkflows" :key="workflow.id" :value="workflow.id">
          {{ workflow.name }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <div class="form-label">Inputs</div>
      <p v-if="!modelValue.workflowId" class="text-muted small mb-0">
        Choose a workflow to see the inputs it reads.
      </p>
      <p v-else-if="inputRows.length === 0" class="text-muted small mb-0">This workflow reads no inputs.</p>
      <div v-for="key in inputRows" :key="key" class="row g-2 mb-2 align-items-start">
        <div class="col-sm-4 font-monospace small text-break pt-sm-2">{{ key }}</div>
        <div class="col-sm-8">
          <BbelField
            :model-value="modelValue.inputs[key] ?? ''"
            :scope="scope"
            single-line
            placeholder="Not set"
            @update:model-value="update({ inputs: withInput(modelValue.inputs, key, $event) })"
          />
        </div>
      </div>
    </div>

    <AdvancedSettings />
  </div>
</template>
